import { Station } from "@/entities/station";
import { VestigingBeschikbareFaciliteiten } from "@/entities/vestiging";
import { VestigingDto } from "@/shared/types";
import { stringToEnumKey } from "@/shared/utils";
import { Openingstijden } from "./openingstijden";

export type BeheerVestiging = {
    id: number;
    externalId?: string; // from nearbyMeLocationId.value
    stationCode: string;
    bedrijfsNaam: string; // kiosk, stationshuiskamer, etc
    vestigingNaam: string; // from places.location.name
    beschikbareFaciliteiten: VestigingBeschikbareFaciliteiten[];
    isNsRetailVestiging: boolean; // Default for vestigingen from Places API
    telefoonnummer?: string;
    opmerkingVestiging?: string;
    openingstijden: Openingstijden[];
    inzetbaar: boolean;
    handmatigToegevoegd: boolean; // Indicates if manually created (true) or from Places API (false)
};

export const BeheerVestiging = {
    getOpeningstijdenVandaag: (beheerVestiging: BeheerVestiging): Openingstijden => {
        return beheerVestiging.openingstijden.find((o: Openingstijden) => o.dag === BeheerVestiging.getDayOfTheWeek()) as Openingstijden;
    },

    createInzetbareStationsFilterFn: (beheerVestigingen: BeheerVestiging[]) => {
        const stationsVoorBeheervestigingen = beheerVestigingen?.reduce((acc, curr) => {
            if (curr.inzetbaar) {
                acc.add(curr.stationCode.toLowerCase());
            }
            return acc;
        }, new Set<string>());

        return (station: Station) => {
            return !!stationsVoorBeheervestigingen?.has(station.code.toLowerCase());
        };
    },

    getDayOfTheWeek: (date: Date = new Date()) => {
        // Map JS weekday (0=Sunday..6=Saturday) to vestiging domain weekday (1=Monday..7=Sunday)
        const jsDay = date.getDay();
        const today = jsDay === 0 ? 7 : jsDay;
        return today;
    },

    isBeschikbaar: (beheerVestiging: BeheerVestiging): boolean => {
        const todayOpeningHours = BeheerVestiging.getOpeningstijdenVandaag(beheerVestiging);
        return !!(beheerVestiging.inzetbaar && todayOpeningHours?.openVan && todayOpeningHours?.openTot);
    },

    isAhToGo: (beheerVestiging: BeheerVestiging) => beheerVestiging.bedrijfsNaam.toLowerCase() === "ah to go",

    filterBeschikaaar: (beheerVestigingen: BeheerVestiging[]): BeheerVestiging[] => beheerVestigingen.filter(BeheerVestiging.isBeschikbaar),

    fromDto: (dto: VestigingDto): BeheerVestiging => ({
        id: dto.id,
        stationCode: dto.stationCode,
        externalId: dto.externalId ?? "",
        bedrijfsNaam: dto.bedrijfsNaam,
        vestigingNaam: dto.vestigingNaam,
        beschikbareFaciliteiten: dto.beschikbareFaciliteiten.map(
            (bf: string) => VestigingBeschikbareFaciliteiten[stringToEnumKey<typeof VestigingBeschikbareFaciliteiten>(bf)]
        ),
        isNsRetailVestiging: dto.isNsRetailVestiging,
        telefoonnummer: dto.telefoonnummer,
        opmerkingVestiging: dto.opmerkingVestiging,
        openingstijden: dto.openingstijden.map(Openingstijden.fromDto),
        inzetbaar: dto.inzetbaar,
        handmatigToegevoegd: dto.handmatigToegevoegd,
    }),
};
